// Game over screen: final score, optional NEW RECORD callout, the updated
// high-score table, and Retry / Title controls. Owns submission of the run's
// score to the high-score store (PlayScene passes the score in, does not submit).

import Phaser from 'phaser';
import { submitScore, topScores, type SubmitResult } from '../storage/high-scores';
import { Button } from '../ui/button';

export interface GameOverData {
  score: number;
  marshmallowsDone: number;
}

export class GameOverScene extends Phaser.Scene {
  private runData!: GameOverData;
  private result!: SubmitResult;

  private heading!: Phaser.GameObjects.Text;
  private recordText!: Phaser.GameObjects.Text;
  private statsText!: Phaser.GameObjects.Text;
  private scoresText!: Phaser.GameObjects.Text;
  private retryBtn!: Button;
  private titleBtn!: Button;

  constructor() {
    super({ key: 'GameOverScene' });
  }

  init(data: GameOverData): void {
    this.runData = { score: data?.score ?? 0, marshmallowsDone: data?.marshmallowsDone ?? 0 };
  }

  create(): void {
    // Submit exactly once, here — GameOverScene owns persistence.
    this.result = submitScore({
      score: this.runData.score,
      marshmallowsDone: this.runData.marshmallowsDone,
      date: new Date().toISOString(),
    });

    this.heading = this.add
      .text(0, 0, 'Game Over', { fontFamily: 'system-ui', fontSize: '48px', color: '#f5e6c8' })
      .setOrigin(0.5);

    this.recordText = this.add
      .text(0, 0, this.result.isNewRecord ? '★ NEW RECORD! ★' : '', {
        fontFamily: 'system-ui',
        fontSize: '24px',
        color: '#ffb347',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const rankLine = this.result.rank ? `   ·   rank #${this.result.rank}` : '';
    this.statsText = this.add
      .text(0, 0, `Score ${this.runData.score}   ·   toasted ${this.runData.marshmallowsDone}${rankLine}`, {
        fontFamily: 'monospace',
        fontSize: '18px',
        color: '#e9d5ae',
        align: 'center',
      })
      .setOrigin(0.5);

    this.scoresText = this.add
      .text(0, 0, this.scoreboard(), {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: '#c8b591',
        align: 'center',
        lineSpacing: 4,
      })
      .setOrigin(0.5, 0);

    this.retryBtn = new Button(this, 0, 0, '↻  Retry', () => this.scene.start('PlayScene'), {
      fill: 0x7a3f16,
      fillHover: 0x9a521e,
      fontSize: 24,
    });
    this.titleBtn = new Button(this, 0, 0, 'Title', () => this.scene.start('TitleScene'), {
      width: 160,
      fontSize: 20,
    });

    this.layout();
    this.scale.on(Phaser.Scale.Events.RESIZE, this.layout, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () =>
      this.scale.off(Phaser.Scale.Events.RESIZE, this.layout, this),
    );
  }

  private scoreboard(): string {
    const scores = topScores(10);
    const rows = scores.map((s, i) => {
      const rank = String(i + 1).padStart(2);
      const score = String(s.score).padStart(6);
      const done = String(s.marshmallowsDone).padStart(3);
      const marker = this.result.rank === i + 1 ? ' ◄' : '';
      return `${rank}.  ${score} pts   ${done} toasted${marker}`;
    });
    return `— best runs —\n${rows.join('\n')}`;
  }

  private layout(): void {
    const { width, height } = this.scale.gameSize;
    const cx = width / 2;
    this.heading.setPosition(cx, height * 0.12);
    this.recordText.setPosition(cx, height * 0.12 + 44);
    this.statsText.setPosition(cx, height * 0.12 + 80);
    this.scoresText.setPosition(cx, height * 0.32);
    this.retryBtn.setPosition(cx, height * 0.8);
    this.titleBtn.setPosition(cx, height * 0.9);
  }
}
